import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Table } from '@tanstack/react-table';
import { Trans, useLingui } from '@lingui/react';
import { Search } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type { FilterConfig, FilterValue, EditableCellConfig } from '../types';
import { useFacetedValues } from '../hooks/useFacetedValues';

interface FilterValuesPanelProps<T> {
  hoveredOption: string;
  hoveredElement: HTMLElement;
  filterConfig: FilterConfig<T>;
  columnLabels: Record<string, string>;
  table: Table<T>;
  columns: EditableCellConfig<T>[];
  onFilterApply: (columnId: string, value: FilterValue) => void;
  onClosePanel: () => void;
}

/**
 * Floating panel that shows filter values positioned next to the hovered filter option
 */
export function FilterValuesPanel<T>({
  hoveredOption,
  hoveredElement,
  filterConfig,
  columnLabels,
  table,
  columns,
  onFilterApply,
  onClosePanel,
}: FilterValuesPanelProps<T>) {
  const { i18n } = useLingui();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Calculate position relative to viewport
  useEffect(() => {
    if (hoveredElement) {
      const rect = hoveredElement.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.right + 8, // 8px gap from the filter option
      });
    }
  }, [hoveredElement]);

  // Get the column configuration for the hovered option
  const columnConfig = useMemo(() => {
    return columns.find(col => col.accessorKey === hoveredOption);
  }, [hoveredOption, columns]);

  // Get faceted values for select filters using shared hook
  const facetedValues = useFacetedValues(hoveredOption, table, filterConfig, columnConfig);

  // Search state for select filter values
  const [selectSearch, setSelectSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset search when the hovered option changes
  useEffect(() => {
    setSelectSearch('');
  }, [hoveredOption]);

  // Filter faceted values by search term
  const filteredFacetedValues = useMemo(() => {
    if (!facetedValues || !selectSearch) return facetedValues;
    const query = selectSearch.toLowerCase();
    return facetedValues.filter(
      (option) => option.label.toLowerCase().includes(query)
    );
  }, [facetedValues, selectSearch]);

  const handleCheckboxChange = useCallback((value: string, checked: boolean) => {
    // Checkbox behavior: keep panel open for multiple selections
    const currentFilter = table.getColumn(hoveredOption)?.getFilterValue() as string[] || [];
    let newValues: string[];
    
    if (checked) {
      newValues = [...currentFilter, value];
    } else {
      newValues = currentFilter.filter(v => v !== value);
    }
    
    onFilterApply(hoveredOption, newValues);
    // Panel stays open for more selections
  }, [hoveredOption, table, onFilterApply]);

  const handleLabelClick = useCallback((value: string) => {
    // Label click behavior: apply filter immediately and close
    onFilterApply(hoveredOption, [value]);
    onClosePanel();
  }, [hoveredOption, onFilterApply, onClosePanel]);

  const handleDateChange = useCallback((newDate: Date | undefined, type: 'from' | 'to') => {
    // Get current date range filter value
    const currentFilter = table.getColumn(hoveredOption)?.getFilterValue() as { from?: Date; to?: Date } || {};
    
    // Merge the new date with existing range
    const updatedRange = {
      from: type === 'from' ? newDate : currentFilter.from,
      to: type === 'to' ? newDate : currentFilter.to,
    };
    
    onFilterApply(hoveredOption, updatedRange);
    // Don't close panel immediately for date selection to allow range building
  }, [hoveredOption, table, onFilterApply]);

  const filterType = filterConfig[hoveredOption as keyof FilterConfig<T>];
  const columnLabel = columnLabels[hoveredOption];

  // Faceted counts are computed from the loaded rows only. Under server-side
  // pagination (manualPagination) the table holds just the current page, so those
  // counts are misleading (e.g. "No (10)" when there are thousands). Hide them in
  // that mode; client-side tables hold every row, so their counts stay accurate.
  const showFacetCounts = table.options.manualPagination !== true;
  
  // Get current filter values based on filter type
  const currentSelectFilter = filterType === 'select' 
    ? table.getColumn(hoveredOption)?.getFilterValue() as string[] || []
    : [];
  
  const currentDateFilter = filterType === 'date'
    ? table.getColumn(hoveredOption)?.getFilterValue() as { from?: Date; to?: Date } || {}
    : {};

  const panelContent = (
    <div
      ref={panelRef}
      className="fixed z-50 bg-popover border border-border rounded-md shadow-lg p-4 min-w-[250px] max-w-[400px]"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <h4 className="text-sm font-medium mb-3">
        <Trans id="table.filter.selectValuesFor" values={{ columnLabel }}>
          Select values for {columnLabel}
        </Trans>
      </h4>
      
      {filterType === 'select' && facetedValues && (
        <div className="space-y-2">
          {facetedValues.length > 5 && (
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder={i18n._({
                  id: 'table.filter.searchPlaceholder',
                  message: 'Search...',
                })}
                aria-label={i18n._({
                  id: 'table.filter.searchValuesAria',
                  message: 'Search filter values',
                })}
                value={selectSearch}
                onChange={(e) => setSelectSearch(e.target.value)}
                className="pl-8 h-9"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {filteredFacetedValues && filteredFacetedValues.length > 0 ? (
              filteredFacetedValues.map((option) => {
                const isChecked = currentSelectFilter.includes(option.value);
                return (
                  <div key={option.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`filter-${hoveredOption}-${option.value}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => handleCheckboxChange(option.value, !!checked)}
                      onClick={(e) => e.stopPropagation()}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <Label
                      htmlFor={`filter-${hoveredOption}-${option.value}`}
                      className="text-sm cursor-pointer flex-1 hover:text-accent-foreground"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLabelClick(option.value);
                      }}
                    >
                      {option.label}{showFacetCounts ? ` (${option.count})` : ''}
                    </Label>
                  </div>
                );
              })
            ) : (
              <div className="py-2 text-sm text-muted-foreground text-center">
                <Trans id="table.filter.noResults">No results</Trans>
              </div>
            )}
          </div>
        </div>
      )}
      
      {filterType === 'date' && (
        <div className="space-y-3">
          <div className="flex w-full items-center gap-2">
            <DatePicker
              value={currentDateFilter.from}
              onChange={(date) => handleDateChange(date, 'from')}
              placeholder={i18n._({
                id: 'table.filter.fromDate',
                message: 'From date',
              })}
              aria-label={i18n._({
                id: 'table.filter.fromDate',
                message: 'From date',
              })}
            />
            <DatePicker
              value={currentDateFilter.to}
              onChange={(date) => handleDateChange(date, 'to')}
              placeholder={i18n._({
                id: 'table.filter.toDate',
                message: 'To date',
              })}
              aria-label={i18n._({
                id: 'table.filter.toDate',
                message: 'To date',
              })}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Render the panel as a portal to avoid z-index issues
  return createPortal(panelContent, document.body);
}